import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import './AccordionSection.scss';
import { RiArrowDownSLine } from 'react-icons/ri';

interface AccordionSectionProps {
  title: string;
  children: React.ReactNode;
  isExpanded?: boolean;
  onToggle?: () => void;
  defaultExpanded?: boolean;
  icon?: React.ReactNode;
  className?: string;
  headerContent?: React.ReactNode;
  fullHeight?: boolean;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  children,
  isExpanded: controlledExpanded,
  onToggle,
  defaultExpanded = true,
  icon,
  className = '',
  headerContent,
  fullHeight = false,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);

  // Use controlled state if provided, otherwise use internal state
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const toggleExpanded = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  }, [onToggle, internalExpanded]);

  return (
    <div
      className={classNames('accordion-section', className, {
        'full-height': fullHeight,
        'expanded-accordion': fullHeight && isExpanded,
      })}
    >
      <div
        className="accordion-section_header"
        onClick={(e) => {
          e.stopPropagation();
          toggleExpanded();
        }}
      >
        <div className="accordion-section_header__content">
          <div className="accordion-section_title">
            {icon && <span className="accordion-section_icon">{icon}</span>}
            <span className="accordion-section_title-text">{title}</span>
          </div>
          {headerContent && (
            <div className="accordion-section_header__extra" onClick={(e) => e.stopPropagation()}>
              {headerContent}
            </div>
          )}
        </div>
        <RiArrowDownSLine
          className={classNames('accordion-section_toggle', {
            expanded: isExpanded,
            collapsed: !isExpanded,
          })}
        />
      </div>
      <div
        className={classNames('accordion-section_content', {
          expanded: isExpanded,
          collapsed: !isExpanded,
        })}
      >
        <div className="accordion-section_content-inner">{children}</div>
      </div>
    </div>
  );
};

export default AccordionSection;
